import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sprout, Leaf, TreePine, Flower2, Sun, Lock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import LevelBadge from "@/components/LevelBadge";
import { ACH_ICONS } from "@/lib/achievements";

const PLANT_TILES = 30;
const PLANT_ICONS = [Sprout, Leaf, Flower2, TreePine];

export default function Garden() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [achievements, setAchievements] = useState([]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/achievements").then((r) => setAchievements(r.data)).catch(() => {});
  }, []);

  const completions = stats?.completions || 0;
  const grown = Math.min(PLANT_TILES, completions);

  return (
    <div className="space-y-8 pb-24 md:pb-8" data-testid="garden-page">
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-emerald-400 mb-2">Your Growth</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Momentum Garden</h1>
        <p className="text-white mt-2">Every habit you complete plants and grows your garden. One tick at a time.</p>
      </div>

      {user && <LevelBadge level={user.level} progress={user.level_progress} need={user.level_need} xp={user.xp} />}

      {/* Garden plot */}
      <div className="relative p-6 sm:p-8 rounded-2xl border border-[#242417] bg-gradient-to-b from-[#0f1a16] to-[#12141C] shadow-xl overflow-hidden">
        <div className="absolute top-4 right-5 flex items-center gap-2 text-amber-400">
          <Sun className="w-6 h-6" />
          <span className="text-sm font-mono-stat">{stats?.best_streak || 0}d sun</span>
        </div>
        <h3 className="font-display text-lg font-semibold mb-6">Your plot · {grown}/{PLANT_TILES} grown</h3>
        <div className="grid grid-cols-6 sm:grid-cols-10 gap-3" data-testid="garden-grid">
          {Array.from({ length: PLANT_TILES }).map((_, i) => {
            const isGrown = i < grown;
            const Icon = PLANT_ICONS[Math.min(PLANT_ICONS.length - 1, Math.floor(i / 8))];
            return (
              <motion.div
                key={i}
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: i * 0.015 }}
                className={`aspect-square rounded-xl flex items-center justify-center border ${
                  isGrown ? "bg-emerald-500/10 border-emerald-500/30" : "bg-white/[0.02] border-[#242417]"
                }`}
              >
                <Icon className={`w-5 h-5 ${isGrown ? "text-emerald-400" : "text-white"}`} />
              </motion.div>
            );
          })}
        </div>
        {grown === 0 && <p className="text-white text-sm mt-5">Complete a habit today to plant your first seed 🌱</p>}
      </div>

      {/* Achievements */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight">Achievements</h2>
          <span className="text-sm text-white font-mono-stat">
            {achievements.filter((a) => a.unlocked).length}/{achievements.length}
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="achievements-grid">
          {achievements.map((a) => {
            const Icon = ACH_ICONS[a.icon] || Sprout;
            return (
              <div
                key={a.id}
                data-testid={`achievement-${a.id}`}
                className={`p-4 rounded-2xl border text-center transition-colors ${
                  a.unlocked ? "border-emerald-500/40 bg-[#12141C]" : "border-[#242417] bg-white/[0.02]"
                }`}
              >
                <div className={`w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-3 ${
                  a.unlocked ? "bg-gradient-to-br from-emerald-500 to-teal-400" : "bg-[#1c1c12]"
                }`}>
                  {a.unlocked ? <Icon className="w-6 h-6 text-black" /> : <Lock className="w-5 h-5 text-white" />}
                </div>
                <div className={`font-semibold text-sm ${a.unlocked ? "" : "text-white"}`}>{a.name}</div>
                <div className="text-xs text-white mt-1">{a.desc}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
